import Dexie, { type EntityTable } from 'dexie';
import { MAX_QUEUE_ITEMS, RESEARCH_DB_NAME } from './constants';
import type { QueuedRecording } from './types';

const db = new Dexie(RESEARCH_DB_NAME) as Dexie & {
  recordings: EntityTable<QueuedRecording, 'id'>;
};

db.version(1).stores({
  recordings: '++id, recordingId, participantId, status, nextAttemptAt, createdAt',
});

export { db as researchQueueDb };

export async function enqueueRecording(
  item: Omit<QueuedRecording, 'id'>
): Promise<number> {
  const pending = await db.recordings.where('status').anyOf(['pending', 'failed']).count();
  if (pending >= MAX_QUEUE_ITEMS) {
    const oldest = await db.recordings
      .where('status')
      .anyOf(['pending', 'failed'])
      .sortBy('createdAt');
    if (oldest[0]?.id !== undefined) {
      await db.recordings.delete(oldest[0].id);
    }
  }
  const id = await db.recordings.add(item);
  if (typeof id !== 'number') {
    throw new Error('failed to enqueue research recording');
  }
  return id;
}

export async function listPending(now = Date.now()): Promise<QueuedRecording[]> {
  const rows = await db.recordings
    .where('status')
    .anyOf(['pending', 'failed'])
    .toArray();
  return rows
    .filter((row) => row.nextAttemptAt <= now)
    .sort((a, b) => a.createdAt - b.createdAt);
}

export async function countQueued(): Promise<{ pending: number; failed: number; uploaded: number }> {
  const rows = await db.recordings.toArray();
  return {
    pending: rows.filter((row) => row.status === 'pending' || row.status === 'uploading').length,
    failed: rows.filter((row) => row.status === 'failed').length,
    uploaded: rows.filter((row) => row.status === 'uploaded').length,
  };
}

export async function markUploading(id: number): Promise<void> {
  await db.recordings.update(id, { status: 'uploading' });
}

export async function markUploaded(id: number): Promise<void> {
  await db.recordings.update(id, { status: 'uploaded', lastError: undefined });
}

export async function markRetry(
  id: number,
  attempts: number,
  error: string,
  nextAttemptAt: number,
  failed: boolean
): Promise<void> {
  await db.recordings.update(id, {
    status: failed ? 'failed' : 'pending',
    attempts,
    lastError: error,
    nextAttemptAt,
  });
}

export async function clearQueue(): Promise<void> {
  await db.recordings.clear();
}

export async function deleteUploadedLocal(): Promise<void> {
  await db.recordings.where('status').equals('uploaded').delete();
}
